import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
    children: ReactNode;
    fallback?: ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
}

/**
 * Top-level crash barrier. React has no hook equivalent, so this stays a class.
 *
 * Catches render-phase errors only — it will not catch errors thrown in event
 * handlers or async callbacks. Handle those where they happen.
 */
export class ErrorBoundary extends Component<
    ErrorBoundaryProps,
    ErrorBoundaryState
> {
    state: ErrorBoundaryState = { hasError: false };

    static getDerivedStateFromError(): ErrorBoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        console.error('[error-boundary] uncaught render error:', error, info);
    }

    render() {
        if (!this.state.hasError) {
            return this.props.children;
        }

        return (
            this.props.fallback ?? (
                <div
                    role="alert"
                    className="flex min-h-dvh flex-col items-center justify-center gap-4 p-8 text-center"
                >
                    <h1 className="text-2xl font-semibold">
                        Something went wrong
                    </h1>
                    <p className="text-surf-200">
                        Reload the page to try again. If it keeps happening, let
                        us know.
                    </p>
                </div>
            )
        );
    }
}
